import { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Legend,
} from "recharts";
import MiniChart from "../components/MiniChart";
import { getJSON } from "../services/api";
import { LIMIT_LOW, LIMIT_HIGH, IDEAL_LOW, IDEAL_HIGH } from "../utils";
import { GroupGuard } from "../auth/GroupGuard";

const REQUIRED_GROUPS = [
  "bcba776b-ad66-4c3c-b253-a10a1b65c294", // Admins - TI
  "ccf4a2fb-4ccb-4292-ba83-6c565146e4f0", // Users - TI
  "1c4b0acb-3657-48c1-9024-0904307a94b7", // CEP - Admins
  "c7b622a4-bdb6-48b4-b39e-9f0970a9602f", // CEP - Users
  "64b8612b-1d6a-479b-a754-b318356dc908", // CEP - Operação
];

const STALE_MIN_DEFAULT = Number(localStorage.getItem("tv_stale_min")) || 15;

type Ponto = {
  idx: number;
  leitura: number;
};

type Bico = {
  bico: number;
  pontos: Ponto[];
  operadora: string;
  ultima_coleta: string;
};

type Metric = {
  b: Bico;
  sev: "CRÍTICO" | "ATENÇÃO" | "OK";
  display: number;
  impact: number;
};

function minsAgo(ts?: string) {
  const d = ts ? new Date(ts) : null;
  if (!d || isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 60000);
}

function fmtDur(mins: number | null) {
  if (mins == null || mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h${String(m).padStart(2, "0")}m` : `${m}m`;
}

function bgBySeverity(s: Metric["sev"]) {
  if (s === "CRÍTICO") return "bg-red-200";
  if (s === "ATENÇÃO") return "bg-yellow-200";
  return "bg-green-200";
}

// Analisa cada bico considerando as 5 últimas leituras
function analyzeBico(b: Bico): Metric {
  const pts = Array.isArray(b?.pontos) ? b.pontos : [];
  const last5 = pts.slice(-5);
  const leituras = last5.map((p) => Number(p?.leitura ?? 0));

  // média das 5
  const media5 = leituras.length
    ? leituras.reduce((a, c) => a + c, 0) / leituras.length
    : 0;

  // procura leituras fora do limite (vermelho)
  let outlier: number | null = null;
  for (const val of leituras) {
    if (val < LIMIT_LOW || val > LIMIT_HIGH) {
      if (!outlier) outlier = val;
      else {
        const curDist = Math.max(
          Math.abs(outlier - LIMIT_LOW),
          Math.abs(outlier - LIMIT_HIGH)
        );
        const newDist = Math.max(
          Math.abs(val - LIMIT_LOW),
          Math.abs(val - LIMIT_HIGH)
        );
        if (newDist > curDist) outlier = val;
      }
    }
  }

  if (outlier != null) {
    return {
      b,
      sev: "CRÍTICO",
      display: outlier,
      impact: Math.max(
        Math.abs(outlier - LIMIT_LOW),
        Math.abs(outlier - LIMIT_HIGH)
      ),
    };
  }

  if (media5 < IDEAL_LOW || media5 > IDEAL_HIGH) {
    return {
      b,
      sev: "ATENÇÃO",
      display: media5,
      impact: Math.max(
        Math.abs(media5 - IDEAL_LOW),
        Math.abs(media5 - IDEAL_HIGH)
      ),
    };
  }

  return { b, sev: "OK", display: media5, impact: 0 };
}

export default function TvMosaico() {
  const [data, setData] = useState<Bico[]>([]);
  const [nextData, setNextData] = useState<Bico[] | null>(null); // buffer
  const [loading, setLoading] = useState(true);
  const [focusIdx, setFocusIdx] = useState(0);
  const [intervalo, setIntervalo] = useState(
    () => Number(localStorage.getItem("tv_intervalo")) || 10000
  );
  const [qtd, setQtd] = useState(
    () => Number(localStorage.getItem("tv_qtd")) || 2
  );
  const [pageIdx, setPageIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let on = true;
    setLoading(true);
    getJSON("/series")
      .then((d) => {
        if (on) setData(Array.isArray(d) ? (d as Bico[]) : []);
      })
      .finally(() => {
        if (on) setLoading(false);
      });
    return () => {
      on = false;
    };
  }, []);

  // 🚀 Atualiza "em background" a cada 45s
  useEffect(() => {
    const bgTimer = setInterval(() => {
      getJSON("/series").then((d) => {
        if (Array.isArray(d)) setNextData(d as Bico[]);
      });
    }, 45000);
    return () => clearInterval(bgTimer);
  }, []);

  // Força o update dos dados com 1 minuto, já tem buffer nesse tempo
  useEffect(() => {
  const forceTimer = setInterval(() => {
    if (nextData) {
      setData(nextData);
      setNextData(null);
      setPageIdx(0);
    }
  }, 60000); // garante refresh a cada 1 min

  return () => clearInterval(forceTimer);
}, [nextData]);

  const metrics = useMemo<Metric[]>(() => {
    return Array.isArray(data) ? data.map(analyzeBico) : [];
  }, [data]);

  const sevOrder = { CRÍTICO: 0, ATENÇÃO: 1, OK: 2 };
  const ordered = useMemo(() => {
    return [...metrics].sort((a, b) => {
      const s = sevOrder[a.sev] - sevOrder[b.sev];
      if (s !== 0) return s;
      return b.impact - a.impact;
    });
  }, [metrics]);

  const problemList = useMemo(
    () => ordered.filter((m) => m.sev === "ATENÇÃO"),
    [ordered]
  );
  const focoMeta = problemList.length
    ? problemList[focusIdx % problemList.length]
    : null;
  const foco = focoMeta?.b;

  const critical = useMemo(
    () => ordered.filter((x) => x.sev === "CRÍTICO"),
    [ordered]
  );
  const nonCritical = useMemo(
    () => ordered.filter((x) => x.sev !== "CRÍTICO"),
    [ordered]
  );
  const metaByBico = useMemo(
    () => new Map(metrics.map((m) => [m.b.bico, m])),
    [metrics]
  );

  const staleMin = STALE_MIN_DEFAULT;
  const alerts = useMemo(() => {
    const parados = data
      .map((b) => {
        const mins = minsAgo(b.ultima_coleta);
        return { bico: b.bico, mins, operadora: b.operadora };
      })
      .filter((x) => x.mins != null && x.mins >= staleMin)
      .sort((a, b) => (b.mins ?? 0) - (a.mins ?? 0));
    return parados.map(
      (x) =>
        `Bico ${String(x.bico).padStart(2, "0")} (há ${fmtDur(x.mins ?? 0)})`
    );
  }, [data, staleMin]);

  const gridItems = useMemo(() => {
    if (!ordered.length) return [];
    const size = Math.max(1, qtd);

    if (critical.length >= size) {
      const pages = Math.ceil(critical.length / size);
      const page = pageIdx % pages;
      const start = page * size;
      return critical.slice(start, start + size).map((m) => m.b);
    }

    const slotsLeft = size - critical.length;
    const pages = Math.ceil((nonCritical.length || 1) / (slotsLeft || 1));
    const page = pages ? pageIdx % pages : 0;
    const start = page * (slotsLeft || 1);
    const rotating = nonCritical.slice(start, start + slotsLeft).map((m) => m.b);
    return [...critical.map((m) => m.b), ...rotating];
  }, [ordered, critical, nonCritical, qtd, pageIdx]);

  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // 🚀 Rotação dos cards
  useEffect(() => {
    if (!ordered.length) return;
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      if (problemList.length) {
        setFocusIdx((i) => (i + 1) % problemList.length);
      }

      const size = Math.max(1, qtd);
      let reachedEnd = false;

      if (critical.length > size) {
        setPageIdx((p) => {
          const next = p + 1;
          reachedEnd = next >= critical.length; // fim de ciclo crítico
          return next;
        });
        return;
      }

      const slotsLeft = Math.max(0, size - critical.length);
      if (slotsLeft > 0 && nonCritical.length > slotsLeft) {
        setPageIdx((p) => {
          const next = p + 1;
          reachedEnd = next >= nonCritical.length; // fim de ciclo não crítico
          return next;
        });
      }

      // 👉 Se chegou ao fim do ciclo, troca os dados pelo buffer
      if (reachedEnd && nextData) {
        setData(nextData);
        setNextData(null);
        setPageIdx(0); // reinicia rotação
      }
    }, intervalo);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [ordered, critical.length, nonCritical.length, intervalo, qtd, problemList.length, nextData]);

  const setIntervaloPersist = (ms: number) => {
    setIntervalo(ms);
    localStorage.setItem("tv_intervalo", String(ms));
  };

  return (
    <GroupGuard allowedGroups={REQUIRED_GROUPS}>
    <section
      className={
        isFs
           ? "fixed inset-0 z-50 overflow-auto bg-white p-2 md:p-4 space-y-3 w-screen h-screen max-w-none"
           : "space-y-3 p-4 md:p-6 bg-white min-h-screen"
      }   
    >
      {/* Ticker de alertas       <Ticker items={alerts} />*/}

      {/* Estado de carregamento ou sem dados */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-10 text-slate-600">
          <div className="h-10 w-10 border-4 border-slate-300 border-t-emerald-600 rounded-full animate-spin"></div>
          <p className="mt-3 text-lg font-medium">Aguarde enquanto os gráficos são preparados …</p>
        </div>
      )}

      {!loading && Array.isArray(data) && data.length === 0 && (
        <div className="text-sm text-slate-500">Sem informações …</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Grade: máx 3 por linha */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {gridItems.map(item => {
            const meta = metaByBico.get(item.bico)
            const val = meta?.display ?? 0
            const sev = meta?.sev ?? 'OK'

            return (
              <div key={item.bico} className={`rounded-2xl p-4 md:p-5 card-shadow opacity-60 ${bgBySeverity(sev)}`} >
                <div className="flex items-center justify-between text-sm md:text-base font-semibold">
                  <span>BICO {String(item.bico).padStart(2, '0')}</span>
                  <span>{sev}</span>
                </div>

                {/* gráfico mais alto e sem espaço embaixo */}
                <div className="w-full h-44 md:h-48 mb-1">
                  <MiniChart data={item.pontos} compact showValues />
                </div>

                {/* texto colado no chart, com tipografia maior */}
                <div className="flex items-baseline gap-3 mt-0">
                  <span className="text-xl md:text-2xl font-bold">Média {val.toFixed(1)}g</span>
                  <span className="text-lg md:text-xl font-semibold opacity-80">Última coleta em {new Date(item.ultima_coleta).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: "UTC"
                })} por {item.operadora}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Spotlight */}
        {!loading && (
        <div className={`rounded-xl card-shadow p-4 flex flex-col ${bgBySeverity(focoMeta?.sev || 'OK')}`}>
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xl font-bold">IMPORTANTE</div>
              <div className="text-sm text-slate-600">Foca no mais crítico, depois atenção; Avança a cada {Math.round(intervalo/1000)}s</div>
            </div>
            <div className="flex gap-2 items-center">
              <label className="text-xs">Cards</label>
              <select
                className="border rounded-md px-2 py-1 text-sm"
                value={qtd}
                onChange={e => {
                  const v = Number(e.target.value)
                  setQtd(v)
                  localStorage.setItem('tv_qtd', String(v))
                  setPageIdx(0)
                }}
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
                <option value={6}>6</option>
                <option value={8}>8</option>
                <option value={10}>10</option>
              </select>

              <label className="text-xs">Troca</label>
              <select
                className="border rounded-md px-2 py-1 text-sm"
                value={intervalo}
                onChange={e => setIntervaloPersist(Number(e.target.value))}
              >
                <option value={5000}>5s</option>
                <option value={10000}>10s</option>
                <option value={15000}>15s</option>
                <option value={20000}>20s</option>
                <option value={30000}>30s</option>
              </select>
            </div>
          </div>

          {foco ? (
            <div className="mt-3">
              <div className="text-2xl font-extrabold text-red-700 mb-2">
                BICO {String(foco.bico).padStart(2,'0')}
              </div>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={foco.pontos}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="idx"
                      tickFormatter={(v) => {
                        const p = (foco?.pontos || [])[v - 1]
                        return p?.leitura != null ? `${p.leitura.toFixed(1)}g` : v
                      }}
                    />
                    <YAxis domain={[LIMIT_LOW - 1, LIMIT_HIGH + 1]} />
                    <Tooltip />
                    <Legend />

                    {/* Linhas fixas */}
                    {/* <ReferenceLine y={LIMIT_HIGH} stroke="red" strokeWidth={1} label="LIMITE MÁX" /> */}
                    <ReferenceLine y={IDEAL_HIGH} stroke="red" strokeWidth={1} label="" />
                    <ReferenceLine y={IDEAL_LOW} stroke="green" strokeWidth={3} label="" />
                    <ReferenceLine y={LIMIT_LOW} stroke="red" strokeWidth={2} label="LIMITE MÍNIMO" />

                    {/* Leitura */}
                    <Line name="Leitura (10 últimos)" type="monotone" dataKey="leitura" stroke="blue" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 text-sm font-bold text-slate-800">
                Última coleta em {new Date(foco.ultima_coleta).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: "UTC"
                })} por {foco.operadora}
              </div>
            </div>
          ) : (
            <div className="mt-3 text-center text-sm text-slate-600">
              Nenhuma anomalia detectada
            </div>
          )}
        </div>
        )}
      </div>
      

      {/* Modo TV: botão de tela cheia */}
      <div className="flex items-center justify-end">
        <button
          className="px-3 py-1 rounded-md border bg-white"
          onClick={() => {
            if (document.documentElement.requestFullscreen) document.documentElement.requestFullscreen()
          }}
        >
          Tela cheia
        </button>
      </div>
    </section>
    </GroupGuard>
  )
}
